import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChartLine,
  FaBullhorn,
  FaAd,
  FaSearch,
  FaCode,
  FaUsers,
  FaNewspaper,
  FaCamera,
  FaPalette,
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaYoutube,
  FaTiktok,
  FaSnapchatGhost,
  FaPinterestP,
  FaTwitter,
  FaRedditAlien,
  FaQuora,
  FaTelegram,
  FaWhatsapp,
  FaPodcast,
  FaCheckCircle,
  FaArrowRight,
  FaWhatsapp as FaWhatsappBtn
} from 'react-icons/fa';
import { SiThreads } from 'react-icons/si';
import { HiSparkles, HiLightningBolt, HiGlobe, HiChip } from 'react-icons/hi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ServicesPage = () => {
  const [activeService, setActiveService] = useState(0);

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to learn more about your services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const services = [
    {
      id: 1,
      icon: FaChartLine,
      title: 'Digital Marketing Strategy',
      scope: 'Brand positioning, funnel & growth roadmap, platform selection',
      platforms: ['All Digital Channels'],
      outcome: 'Clear roadmap to achieve business goals with measurable KPIs',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500/10',
      iconBg: 'bg-blue-500',
    },
    {
      id: 2,
      icon: FaBullhorn,
      title: 'Social Media Marketing',
      scope: 'Content calendars, posting & engagement, community building',
      platforms: ['Instagram', 'Facebook', 'LinkedIn', 'YouTube', 'TikTok', 'Twitter'],
      outcome: 'Consistent brand presence with engaged community and growing reach',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500/10',
      iconBg: 'bg-pink-500',
    },
    {
      id: 3,
      icon: FaAd,
      title: 'Performance Marketing & Ads',
      scope: 'Campaign setup, creative testing, budget & ROI optimization, detailed analytics',
      platforms: ['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'Retargeting'],
      outcome: 'Maximum ROI with data-driven campaigns and continuous optimization',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-500/10',
      iconBg: 'bg-orange-500',
    },
    {
      id: 4,
      icon: FaSearch,
      title: 'SEO',
      scope: 'On-page, technical, off-page, local SEO, authority building via PR',
      platforms: ['Google', 'Bing', 'Local Search'],
      outcome: 'Higher rankings, increased organic traffic, sustainable visibility',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500/10',
      iconBg: 'bg-green-500',
    },
    {
      id: 5,
      icon: FaCode,
      title: 'Website Development & Hosting',
      scope: 'Business, e-commerce, landing pages, SEO-ready & conversion-focused, hosting, security, maintenance',
      platforms: ['Custom Development', 'WordPress', 'Shopify'],
      outcome: 'Fast, secure, conversion-optimized websites that drive business growth',
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500/10',
      iconBg: 'bg-purple-500',
    },
    {
      id: 6,
      icon: FaUsers,
      title: 'Influencer Marketing',
      scope: 'Creator discovery, campaign execution, performance tracking',
      platforms: ['Instagram', 'YouTube', 'TikTok'],
      outcome: 'Authentic brand partnerships with measurable impact',
      color: 'from-cyan-500 to-teal-600',
      bgColor: 'bg-cyan-500/10',
      iconBg: 'bg-cyan-500',
    },
    {
      id: 7,
      icon: FaNewspaper,
      title: 'PR & Brand Visibility',
      scope: 'Press releases, media placements, founder & brand authority',
      platforms: ['Media Outlets', 'Publications', 'News Platforms'],
      outcome: 'Enhanced credibility and widespread brand recognition',
      color: 'from-red-500 to-rose-600',
      bgColor: 'bg-red-500/10',
      iconBg: 'bg-red-500',
    },
    {
      id: 8,
      icon: FaCamera,
      title: 'Creative Production',
      scope: 'Photoshoot & videoshoot, video editing, graphic designing',
      platforms: ['All Channels'],
      outcome: 'Professional-grade visuals that capture attention and drive engagement',
      color: 'from-fuchsia-500 to-pink-600',
      bgColor: 'bg-fuchsia-500/10',
      iconBg: 'bg-fuchsia-500',
    },
    {
      id: 9,
      icon: FaPalette,
      title: 'Digital & Print Design',
      scope: 'E-books, PDFs, pitch decks, brochures, flyers, hoardings, packaging',
      platforms: ['Digital & Physical'],
      outcome: 'Consistent brand identity across all touchpoints',
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-500/10',
      iconBg: 'bg-indigo-500',
    },
    {
      id: 10,
      icon: FaCamera,
      title: 'Photo & Video Shoot',
      scope: 'Professional photography, product shoots, commercials, reels, corporate videos, and high-end video editing.',
      platforms: ['Instagram', 'YouTube', 'Website', 'Offline Ads'],
      outcome: 'Stunning high-quality visuals that tell your brand story and drive conversions.',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-500/10',
      iconBg: 'bg-orange-500',
    },
  ];

  // Auto-cycle through services every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % 10); // 10 services total
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const platforms = [
    { icon: FaInstagram, name: 'Instagram', color: 'hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-500 hover:to-orange-400' },
    { icon: FaFacebookF, name: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: FaLinkedinIn, name: 'LinkedIn', color: 'hover:bg-blue-700' },
    { icon: FaYoutube, name: 'YouTube', color: 'hover:bg-red-600' },
    { icon: FaTiktok, name: 'TikTok', color: 'hover:bg-black' },
    { icon: FaSnapchatGhost, name: 'Snapchat', color: 'hover:bg-yellow-400' },
    { icon: FaPinterestP, name: 'Pinterest', color: 'hover:bg-red-700' },
    { icon: SiThreads, name: 'Threads', color: 'hover:bg-black' },
    { icon: FaTwitter, name: 'Twitter', color: 'hover:bg-sky-500' },
    { icon: FaRedditAlien, name: 'Reddit', color: 'hover:bg-orange-600' },
    { icon: FaQuora, name: 'Quora', color: 'hover:bg-red-800' },
    { icon: FaTelegram, name: 'Telegram', color: 'hover:bg-blue-500' },
    { icon: FaWhatsapp, name: 'WhatsApp', color: 'hover:bg-green-500' },
    { icon: FaPodcast, name: 'Podcasts', color: 'hover:bg-purple-600' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-1/2 -left-1/2 w-full h-full"
          >
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />
          </motion.div>

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <HiSparkles className="text-yellow-400 text-lg" />
              <span className="text-white font-semibold text-sm">Our Services</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Comprehensive{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                Digital Solutions
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto"
            >
              From strategy to execution, we cover every aspect of your digital growth journey
            </motion.p>

            {/* Floating Icons */}
            <div className="flex justify-center gap-4 mt-10 mb-16">
              {[HiLightningBolt, HiGlobe, HiChip].map((Icon, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20"
                >
                  <Icon className="text-2xl text-white" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-16">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-100/50 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                onHoverStart={() => setActiveService(index)}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${service.color} rounded-3xl opacity-0 group-hover:opacity-70 blur-xl transition-all duration-500`} />

                <div className="relative bg-white rounded-3xl p-8 shadow-lg border border-slate-100 group-hover:border-transparent group-hover:shadow-2xl transition-all duration-500 h-full">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className={`w-16 h-16 ${service.iconBg} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-xl transition-shadow`}
                  >
                    <service.icon className="text-2xl text-white" />
                  </motion.div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-slate-900 group-hover:to-slate-700 transition-all">
                    {service.title}
                  </h3>

                  {/* Scope */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scope</span>
                    <p className="text-slate-600 text-sm mt-1">{service.scope}</p>
                  </div>

                  {/* Platforms */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Platforms</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {service.platforms.map((platform, i) => (
                        <span
                          key={i}
                          className={`px-3 py-1 ${service.bgColor} text-slate-700 text-xs font-medium rounded-full`}
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Outcome */}
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex items-start gap-2">
                      <FaCheckCircle className={`text-lg mt-0.5 flex-shrink-0`} style={{ color: service.iconBg.replace('bg-', '').replace('-500', '') }} />
                      <div>
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Outcome</span>
                        <p className="text-slate-700 text-sm font-medium">{service.outcome}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hover Arrow */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileHover={{ opacity: 1, x: 0 }}
                    className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <div className={`w-10 h-10 bg-gradient-to-r ${service.color} rounded-full flex items-center justify-center shadow-lg`}>
                      <FaArrowRight className="text-white text-sm" />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Platform Mastery Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-50">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #e2e8f0 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full text-blue-600 text-sm font-semibold mb-4"
            >
              Platform Expertise
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Platform{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Mastery
              </span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We're experts across all major platforms
            </p>
          </motion.div>

          {/* Platforms Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4"
          >
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{
                  y: -10,
                  scale: 1.1,
                  transition: { duration: 0.2 }
                }}
                className="group"
              >
                <div className={`relative bg-white rounded-2xl p-6 shadow-md border border-slate-100 flex flex-col items-center gap-3 cursor-pointer transition-all duration-300 ${platform.color} hover:text-white hover:border-transparent hover:shadow-xl`}>
                  <platform.icon className="text-3xl text-slate-600 group-hover:text-white transition-colors" />
                  <span className="text-xs font-medium text-slate-600 group-hover:text-white transition-colors text-center">
                    {platform.name}
                  </span>

                  {/* Shine Effect */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden">
                    <motion.div
                      initial={{ x: '-100%', opacity: 0 }}
                      whileHover={{ x: '100%', opacity: 0.3 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent skew-x-12"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Interactive Service Showcase */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Explore Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                Services
              </span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Click on any service to see what we can do for you
            </p>
          </motion.div>

          {/* Interactive Cards */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Service Selector */}
            <div className="space-y-3">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setActiveService(index)}
                  className={`cursor-pointer p-4 rounded-2xl transition-all duration-300 ${activeService === index
                      ? `bg-gradient-to-r ${service.color} shadow-lg`
                      : 'bg-white/5 hover:bg-white/10'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeService === index ? 'bg-white/20' : service.iconBg
                      }`}>
                      <service.icon className="text-xl text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold">{service.title}</h4>
                      <p className={`text-sm ${activeService === index ? 'text-white/80' : 'text-slate-400'}`}>
                        {service.platforms.slice(0, 3).join(' • ')}
                      </p>
                    </div>
                    <FaArrowRight className={`text-white transition-transform ${activeService === index ? 'translate-x-1' : ''
                      }`} />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Active Service Detail */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="sticky top-32"
              >
                <div className={`bg-gradient-to-br ${services[activeService].color} rounded-3xl p-8 shadow-2xl`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      {React.createElement(services[activeService].icon, { className: 'text-3xl text-white' })}
                    </div>
                    <h3 className="text-2xl font-bold text-white">{services[activeService].title}</h3>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-2">What We Do</h4>
                      <p className="text-white text-lg">{services[activeService].scope}</p>
                    </div>

                    <div>
                      <h4 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-3">Platforms</h4>
                      <div className="flex flex-wrap gap-2">
                        {services[activeService].platforms.map((platform, i) => (
                          <motion.span
                            key={i}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full"
                          >
                            {platform}
                          </motion.span>
                        ))}
                      </div>
                    </div>

                    <div className="pt-6 border-t border-white/20">
                      <h4 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-2">Expected Outcome</h4>
                      <div className="flex items-start gap-3">
                        <FaCheckCircle className="text-white text-xl mt-1 flex-shrink-0" />
                        <p className="text-white text-lg font-medium">{services[activeService].outcome}</p>
                      </div>
                    </div>

                    <motion.button
                      onClick={openWhatsApp}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full mt-4 py-4 bg-white text-slate-900 font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                    >
                      <FaWhatsappBtn className="text-xl text-green-500" />
                      Get Started with This Service
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full border border-blue-500/20 mb-8"
            >
              <HiSparkles className="text-blue-600" />
              <span className="text-blue-700 font-medium">Ready to Grow?</span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Let's Transform Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Digital Presence
              </span>
            </h2>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              Get a free consultation and discover how we can help your business grow
            </p>

            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-full shadow-2xl transition-all cursor-pointer group"
            >
              <FaWhatsappBtn className="text-2xl" />
              Start Your Journey
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
              </motion.span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
